import React from 'react';
import { Check, X } from 'lucide-react';

export interface PasswordStrength {
  score: number; // 0-4 (weak to very strong)
  feedback: string[];
  isValid: boolean;
}

interface PasswordStrengthIndicatorProps {
  password: string;
  minLength?: number;
  showRequirements?: boolean;
  className?: string;
}

/**
 * Calculate password strength based on various criteria
 */
const calculatePasswordStrength = (password: string, minLength: number = 8): PasswordStrength => {
  const feedback: string[] = [];
  let score = 0;

  // Length check
  if (password.length < minLength) {
    feedback.push(`At least ${minLength} characters`);
  } else {
    score += 1;
  }

  // Uppercase letter
  if (!/[A-Z]/.test(password)) {
    feedback.push('One uppercase letter');
  } else {
    score += 1;
  }

  // Lowercase letter
  if (!/[a-z]/.test(password)) {
    feedback.push('One lowercase letter');
  } else {
    score += 1;
  }

  // Number
  if (!/\d/.test(password)) {
    feedback.push('One number');
  } else {
    score += 1;
  }

  // Special character
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)) {
    feedback.push('One special character (!@#$%^&*)');
  } else {
    score += 1;
  }

  // Bonus for length
  if (password.length >= 12) {
    score += 1;
  }

  return {
    score: Math.min(score, 4),
    feedback,
    isValid: password.length >= minLength
  };
};

/**
 * Get strength label and color based on score
 */
const getStrengthInfo = (score: number) => {
  switch (score) {
    case 0:
    case 1:
      return { label: 'Weak', color: 'bg-red-500', textColor: 'text-red-600' };
    case 2:
      return { label: 'Fair', color: 'bg-yellow-500', textColor: 'text-yellow-600' };
    case 3:
      return { label: 'Good', color: 'bg-blue-500', textColor: 'text-blue-600' };
    case 4:
    default:
      return { label: 'Strong', color: 'bg-green-500', textColor: 'text-green-600' };
  }
};

/**
 * Password strength indicator component with visual feedback
 */
export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  minLength = 8,
  showRequirements = true,
  className = ''
}) => {
  const strength = calculatePasswordStrength(password, minLength);
  const strengthInfo = getStrengthInfo(strength.score);

  // Don't show anything if password is empty
  if (!password) {
    return null;
  }

  return (
    <div className={`mt-2 ${className}`}>
      {/* Strength bar */}
      <div className="flex items-center space-x-2 mb-2">
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div
            className={`h-2 rounded-full transition-all duration-300 ${strengthInfo.color}`}
            style={{ width: `${(strength.score / 4) * 100}%` }}
          />
        </div>
        <span className={`text-sm font-medium ${strengthInfo.textColor}`}>
          {strengthInfo.label}
        </span>
      </div>

      {/* Requirements checklist */}
      {showRequirements && (
        <div className="space-y-1">
          <RequirementItem
            met={password.length >= minLength}
            text={`At least ${minLength} characters`}
          />
          <RequirementItem
            met={/[A-Z]/.test(password)}
            text="One uppercase letter"
          />
          <RequirementItem
            met={/[a-z]/.test(password)}
            text="One lowercase letter"
          />
          <RequirementItem
            met={/\d/.test(password)}
            text="One number"
          />
          <RequirementItem
            met={/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password)}
            text="One special character"
          />
        </div>
      )}
    </div>
  );
};

/**
 * Individual requirement item component
 */
const RequirementItem: React.FC<{ met: boolean; text: string }> = ({ met, text }) => (
  <div className="flex items-center space-x-2 text-sm">
    {met ? (
      <Check className="h-4 w-4 text-green-500" />
    ) : (
      <X className="h-4 w-4 text-gray-400" />
    )}
    <span className={met ? 'text-green-600' : 'text-gray-500'}>
      {text}
    </span>
  </div>
);

/**
 * Simplified password strength indicator for inline use
 */
export const SimplePasswordStrengthIndicator: React.FC<{
  password: string;
  minLength?: number;
  className?: string;
}> = ({ password, minLength = 8, className = '' }) => {
  const strength = calculatePasswordStrength(password, minLength);
  const strengthInfo = getStrengthInfo(strength.score);

  if (!password) {
    return null;
  }

  return (
    <div className={`flex items-center space-x-2 mt-1 ${className}`}>
      <div className="flex-1 bg-gray-200 rounded-full h-1">
        <div
          className={`h-1 rounded-full transition-all duration-300 ${strengthInfo.color}`}
          style={{ width: `${(strength.score / 4) * 100}%` }}
        />
      </div>
      <span className={`text-xs ${strengthInfo.textColor}`}>
        {strengthInfo.label}
      </span>
    </div>
  );
};

export default PasswordStrengthIndicator;
